/**
 * A small windowed list (M6 plan P28; no dependency): a fixed-height scroll box with a spacer as tall as every row
 * and only the window's rows (plus `overscan` on each side) rendered at their offsets. The owner keeps the window's
 * first row (`first`, from `fleetModel.followCursor`); the box scrolls to it when it changes, and a wheel or drag
 * scroll reports the new first row through `onScroll`. UI events only.
 */
import { useLayoutEffect, useRef, type ReactNode } from 'react'
import { windowSlice } from './fleetModel'

export interface WindowedListProps {
  count: number
  /** Row height in px (every row is this tall). */
  rowHeight: number
  /** Rows the box shows at once. */
  visibleRows: number
  overscan: number
  /** The window's first row. */
  first: number
  renderRow: (index: number) => ReactNode
  /** The user scrolled: the new first row (rounded down). */
  onScroll?: (first: number) => void
  className?: string
  testId?: string
  /** Shown when `count` is 0. */
  empty?: ReactNode
  label?: string
}

export function WindowedList(p: WindowedListProps): ReactNode {
  const box = useRef<HTMLDivElement>(null)
  const [start, end] = windowSlice(p.first, p.visibleRows, p.overscan, p.count)
  const height = p.visibleRows * p.rowHeight

  useLayoutEffect(() => {
    const el = box.current
    if (el === null) return
    const top = p.first * p.rowHeight
    // Only move the box when the owner's window left the scrolled rows (a user scroll reports first, not this).
    if (Math.floor(el.scrollTop / p.rowHeight) !== p.first) el.scrollTop = top
  }, [p.first, p.rowHeight])

  const rows: ReactNode[] = []
  for (let i = start; i < end; i++) {
    rows.push(
      <div key={i} className="fl-wl__row" role="row" aria-rowindex={i + 1} style={{ top: i * p.rowHeight, height: p.rowHeight }}>
        {p.renderRow(i)}
      </div>,
    )
  }
  return (
    <div
      ref={box}
      className={`fl-wl ${p.className ?? ''}`}
      role="grid"
      aria-rowcount={p.count}
      aria-label={p.label}
      data-testid={p.testId}
      style={{ height, overflowY: 'auto', position: 'relative' }}
      onScroll={(e) => p.onScroll?.(Math.floor(e.currentTarget.scrollTop / p.rowHeight))}
    >
      {p.count === 0 ? (
        p.empty
      ) : (
        <div className="fl-wl__spacer" style={{ height: p.count * p.rowHeight, position: 'relative' }}>
          {rows}
        </div>
      )}
    </div>
  )
}
